import PageHero from "@/components/PageHero";
import SectionLabel from "@/components/SectionLabel";
import EditorialBanner from "@/components/EditorialBanner";
import { Quote } from "lucide-react";

const DIFFS = [
  {
    a: "Black-box ML scoring",
    b: "Deterministic, reproducible scoring",
    note: "Same input, same output. No drift, no surprise.",
  },
  {
    a: "Vendor lock-in",
    b: "Vendor-agnostic by configuration",
    note: "Swap KYC, AML, TM or fraud vendors without touching code.",
  },
  {
    a: "Per-tenant blind spots",
    b: "Entity resolution across CIFs, tenants, jurisdictions and corporates",
    note: "M&A duplicates, multi-system universal banks, UBO networks, BaaS tenants.",
  },
  {
    a: "US-hosted SaaS",
    b: "EU-resident, DORA-ready",
    note: "AWS Stockholm. Per-tenant key management. Article 30 from day one.",
  },
  {
    a: "Action-taking AI",
    b: "Advisory only by construction",
    note: "Sentinel never blocks. EU AI Act Article 14, by design.",
  },
  {
    a: "Opaque audit trail",
    b: "Hash-chained ledger",
    note: "Append-only. Tamper-evident. Every decision defensible.",
  },
];

export default function WhyLexifide() {
  return (
    <div data-testid="why-page">
      <PageHero
        eyebrow="Why Lexifide.ai"
        title="A product about two outcomes. Not nineteen."
        sub="Sentinel exists to solve two things — false-positive reduction across AML and fraud, and missed risk that hides without entity resolution and cross-system context. Designed in conversation with EU mid-tier banks, to answer the question a supervisor is going to ask before they ask it."
        breadcrumb={[{ label: "Lexifide.ai", to: "/" }, { label: "Why Lexifide" }]}
        testId="why-hero"
      />

      {/* Differentiators */}
      <section className="light-section py-24 lg:py-32">
        <div className="max-w-[1380px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
            <div className="lg:col-span-7">
              <SectionLabel index="01" label="The six lines we won't cross" />
              <h2 className="mt-6 font-display font-bold text-[#090a0f] text-4xl sm:text-5xl lg:text-[56px] tracking-tight leading-[1.02]">
                Six commitments,<br />
                <span className="text-[#52525b]">written into the product.</span>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-[#52525b] text-[17px] leading-relaxed">
                Each commitment is enforced by the architecture — not a
                policy document or a launch slide.
              </p>
            </div>
          </div>

          <EditorialBanner
            variant="light"
            gradient="paper-violet"
            image="https://images.unsplash.com/photo-1496181133206-80ce9b88a853?auto=format&fit=crop&w=2400&q=80"
            kicker="Six commitments"
            headline="Defensibility is a design decision — not a launch slide."
            chips={[
              { c: "#0047ff", t: "Deterministic" },
              { c: "#7c3aed", t: "Advisory only" },
              { c: "#10b981", t: "EU-resident" },
            ]}
            testId="why-diffs-banner"
          />

          <div className="border border-[#e4e4e7]">
            {DIFFS.map((d, i) => {
              const ACCENTS = ["#0047ff", "#00b8c9", "#7c3aed", "#f59e0b", "#10b981", "#f43f5e"];
              const hex = ACCENTS[i % ACCENTS.length];
              return (
                <div
                  key={d.b}
                  data-testid={`why-diff-${i}`}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-6 lg:p-8 border-b border-[#e4e4e7] last:border-b-0 group hover:bg-[#fafafa] transition-colors relative"
                >
                  <span
                    className="absolute left-0 top-0 bottom-0 w-[3px]"
                    style={{ backgroundColor: hex }}
                  />
                  <div className="lg:col-span-1">
                    <span
                      className="font-mono text-[11px] tracking-[0.22em] font-semibold"
                      style={{ color: hex }}
                    >
                      0{i + 1}
                    </span>
                  </div>
                  <div className="lg:col-span-4">
                    <p className="text-[#a1a1aa] line-through text-[15px]">{d.a}</p>
                  </div>
                  <div className="lg:col-span-4">
                    <p className="font-display font-bold text-[#090a0f] text-[18px] tracking-tight">
                      {d.b}
                  </p>
                </div>
                <div className="lg:col-span-3">
                  <p className="text-[#52525b] text-[13.5px] leading-relaxed">{d.note}</p>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission / About */}
      <section className="dark-section py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-40" />
        <div className="relative max-w-[1380px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <SectionLabel index="02" label="Our mission" dark />
              <h2 className="mt-6 font-display font-bold text-white text-4xl sm:text-5xl lg:text-[52px] tracking-tight leading-[1.02]">
                Compliance you can defend at 4&thinsp;a.m.<br />
                <span className="text-white/55">on a Sunday.</span>
              </h2>
            </div>
            <div className="lg:col-span-7">
              <div className="border-l-2 border-[#00e5ff] pl-6">
                <Quote className="w-7 h-7 text-[#00e5ff] mb-4" />
                <p className="font-display text-white text-[22px] lg:text-[26px] leading-snug tracking-tight">
                  &ldquo;The structural risk in a bank&rsquo;s compliance doesn&rsquo;t live
                  inside a single compliance solution. It lives between the
                  different compliance solutions that exist in the bank. We
                  built Sentinel to read those gaps, with the kind of audit
                  trail that lets you sleep through a supervisory visit.&rdquo;
                </p>
                <p className="mt-6 font-mono text-[11px] tracking-[0.22em] uppercase text-white/55">
                  Lexifide.ai Founding Team — Stockholm
                </p>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-px bg-white/10 border border-white/10">
                {[
                  { k: "2026", v: "Founded" },
                  { k: "EU-only", v: "Data residency" },
                  { k: "100%", v: "Deterministic core" },
                ].map((c) => (
                  <div key={c.v} className="bg-[#0d0e16] p-6">
                    <p className="font-display font-bold text-white text-[28px] tracking-tight tabular">{c.k}</p>
                    <p className="mt-1 text-white/55 text-[13px]">{c.v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roadmap teaser */}
      <section className="light-section py-24 lg:py-32 border-t border-[#e4e4e7]">
        <div className="max-w-[1380px] mx-auto px-6 lg:px-10">
          <SectionLabel index="03" label="Where we go next" />
          <h2 className="mt-6 font-display font-bold text-[#090a0f] text-4xl sm:text-5xl lg:text-[52px] tracking-tight leading-[1.02] max-w-[18ch]">
            Advisory AI, on rails the regulator already trusts.
          </h2>

          <div className="mt-12">
          <EditorialBanner
            variant="light"
            gradient="paper-violet"
            image="https://images.unsplash.com/photo-1568992687947-868a62a9f521?auto=format&fit=crop&w=2400&q=80"
            kicker="Forthcoming"
            headline="Advisory agents — built the same way."
            chips={[
              { c: "#00e5ff", t: "Regulatory RAG" },
              { c: "#7c3aed", t: "Rule Generation" },
              { c: "#f59e0b", t: "SAR Drafting" },
            ]}
            testId="why-roadmap-banner"
          />
          </div>

          <div className="mt-px grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e4e4e7] border border-[#e4e4e7]">
            {[
              { q: "Q1", t: "Regulatory RAG", b: "Cite the exact provision behind every score." },
              { q: "Q2", t: "Rule Generation Agent", b: "Propose typology rules from prior cases — officer approves." },
              { q: "Q3", t: "SAR Drafting Agent", b: "99%+ field validation. Immutable data retention." },
            ].map((r) => (
              <div key={r.t} className="bg-white p-7 min-h-[200px]">
                <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#0047ff]">{r.q}</p>
                <p className="mt-3 font-display font-bold text-[#090a0f] text-[22px] tracking-tight">{r.t}</p>
                <p className="mt-2 text-[#52525b] text-[14px] leading-relaxed">{r.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
